/**
 * Catalog Item Row Component
 *
 * Displays a single catalog item with inline editing capabilities.
 */

import { useState, useMemo, useEffect } from 'react';
import { History, Trash2, Check, X, Info, AlertTriangle } from 'lucide-react';
import { Button, Input, Select } from '@/components/ui';
import { formatCentimes, formatQuantity, getQuantityStep } from '@/lib/format';
import { getConversionInfo, ConversionMode } from '@bstock/shared';
import { cn } from '@/lib/utils';
import type { SupplierCatalogItemWithDetails, PackagingOption } from '@bstock/shared';
import type { useUpdateCatalogItem, useUpdateCatalogItemPrice, useDeleteCatalogItem } from '../../hooks/useCatalog';

interface CatalogItemRowProps {
  item: SupplierCatalogItemWithDetails;
  supplierId: string;
  recipeUnitSymbol: string;
  packagings: PackagingOption[];
  currency: string;
  isEditing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onViewHistory: () => void;
  updateMutation: ReturnType<typeof useUpdateCatalogItem>;
  updatePriceMutation: ReturnType<typeof useUpdateCatalogItemPrice>;
  deleteMutation: ReturnType<typeof useDeleteCatalogItem>;
}

export function CatalogItemRow({
  item,
  recipeUnitSymbol,
  packagings,
  currency,
  isEditing,
  onEdit,
  onCancelEdit,
  onViewHistory,
  updateMutation,
  updatePriceMutation,
  deleteMutation,
}: CatalogItemRowProps) {
  const currentPrice = item.currentPrice?.priceExclTax ?? 0;

  // Edit state
  const [editValues, setEditValues] = useState({
    packagingId: item.packagingId,
    conversionToRecipeUnit: String(item.conversionToRecipeUnit),
    priceExclTax: String(currentPrice / 100),
    supplierRef: item.supplierRef ?? '',
  });

  // Ensure current item's packaging is in the list (even if inactive)
  const packagingOptions = useMemo(() => {
    const currentPackagingInList = packagings.some((p) => p.id === item.packagingId);
    if (currentPackagingInList) {
      return packagings;
    }
    // Add current packaging if not in list (it may be inactive)
    return [...packagings, item.packaging];
  }, [packagings, item.packaging, item.packagingId]);

  // Get selected packaging details for display
  const selectedPackaging = packagingOptions.find((p) => p.id === editValues.packagingId) ?? item.packaging;

  // Calculate conversion info based on selected packaging and raw product recipe unit
  const conversionInfo = useMemo(() => {
    if (!selectedPackaging) return null;

    return getConversionInfo(
      selectedPackaging.quantity,
      selectedPackaging.unit,
      item.rawProduct.recipeUnit
    );
  }, [selectedPackaging, item.rawProduct.recipeUnit]);

  // Synchronize auto-calculated value with edit state when packaging changes
  useEffect(() => {
    if (
      conversionInfo &&
      (conversionInfo.mode === ConversionMode.AUTO || conversionInfo.mode === ConversionMode.NONE) &&
      conversionInfo.value !== null
    ) {
      setEditValues((prev) => ({
        ...prev,
        conversionToRecipeUnit: String(conversionInfo.value),
      }));
    }
  }, [conversionInfo]);

  const isLoading =
    updateMutation.isPending || updatePriceMutation.isPending || deleteMutation.isPending;

  const handleSave = async () => {
    const conversionValue = parseFloat(editValues.conversionToRecipeUnit);
    const priceValue = parseFloat(editValues.priceExclTax);

    if (isNaN(conversionValue) || isNaN(priceValue)) return;

    try {
      // Update item details (only if packaging changed or conversion/supplierRef changed)
      if (
        editValues.packagingId !== item.packagingId ||
        conversionValue !== item.conversionToRecipeUnit ||
        editValues.supplierRef !== (item.supplierRef ?? '')
      ) {
        await updateMutation.mutateAsync({
          itemId: item.id,
          data: {
            packagingId: editValues.packagingId,
            conversionToRecipeUnit: conversionValue,
            supplierRef: editValues.supplierRef || null,
          },
        });
      }

      // Update price if changed
      const newPriceCents = Math.round(priceValue * 100);
      if (newPriceCents !== currentPrice) {
        await updatePriceMutation.mutateAsync({
          itemId: item.id,
          data: { priceExclTax: newPriceCents },
        });
      }

      onCancelEdit();
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer cette reference ?')) return;
    try {
      await deleteMutation.mutateAsync(item.id);
    } catch {
      // Error handled by mutation
    }
  };

  if (isEditing) {
    return (
      <div className="bg-white rounded-lg px-4 py-3 border-2 border-primary-300">
        <div className="grid grid-cols-4 gap-3 mb-3">
          {/* Conditionnement Select */}
          <div>
            <Select
              label="Conditionnement"
              value={editValues.packagingId}
              onChange={(value) => setEditValues({ ...editValues, packagingId: value })}
              options={packagingOptions.map((p) => ({
                value: p.id,
                label: `${p.name} (${formatQuantity(p.quantity)} ${p.unit.symbol})`,
              }))}
              allowEmpty={false}
            />
          </div>
          <div>
            <Input
              label={`Conversion (${recipeUnitSymbol})`}
              type="number"
              step={getQuantityStep()}
              min="0.01"
              value={editValues.conversionToRecipeUnit}
              onChange={(e) =>
                setEditValues({ ...editValues, conversionToRecipeUnit: e.target.value })
              }
              disabled={conversionInfo ? !conversionInfo.isEditable : false}
              className={conversionInfo && !conversionInfo.isEditable ? 'bg-neutral-50 cursor-not-allowed' : ''}
            />
            {/* Conversion info message */}
            {conversionInfo && (
              <div
                className={cn(
                  'mt-1 text-xs flex items-start gap-1',
                  conversionInfo.mode === ConversionMode.AUTO && 'text-success-600',
                  conversionInfo.mode === ConversionMode.NONE && 'text-neutral-500',
                  conversionInfo.mode === ConversionMode.MANUAL && 'text-warning-600'
                )}
              >
                {conversionInfo.mode === ConversionMode.AUTO && (
                  <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                )}
                {conversionInfo.mode === ConversionMode.NONE && (
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                )}
                {conversionInfo.mode === ConversionMode.MANUAL && (
                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                )}
                <span>{conversionInfo.message}</span>
              </div>
            )}
          </div>
          <Input
            label={`Prix HT (${currency})`}
            type="number"
            step="0.01"
            min="0"
            value={editValues.priceExclTax}
            onChange={(e) => setEditValues({ ...editValues, priceExclTax: e.target.value })}
          />
          <Input
            label="Ref. fournisseur"
            value={editValues.supplierRef}
            onChange={(e) => setEditValues({ ...editValues, supplierRef: e.target.value })}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEdit} disabled={isLoading}>
            <X className="w-4 h-4 mr-1" />
            Annuler
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={isLoading}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} isLoading={isLoading}>
            <Check className="w-4 h-4 mr-1" />
            Enregistrer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center bg-white rounded-lg px-4 py-3 border border-neutral-200 hover:border-neutral-300 cursor-pointer transition-colors"
      onClick={onEdit}
    >
      {/* Packaging Name */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-neutral-900 truncate">{item.packaging.name}</p>
        {item.supplierRef && <p className="text-xs text-neutral-500 truncate">Ref: {item.supplierRef}</p>}
      </div>

      {/* Conversion */}
      <div className="flex-[2] text-sm text-neutral-600 text-center whitespace-nowrap">
        1 {item.packaging.name} = {formatQuantity(item.conversionToRecipeUnit)} {recipeUnitSymbol}
      </div>

      {/* Price HT / conditionnement */}
      <div className="flex-1 flex items-baseline justify-end gap-1 whitespace-nowrap">
        <span className="font-semibold text-neutral-900">{formatCentimes(currentPrice, currency)}</span>
        <span className="text-xs text-neutral-500">HT / {item.packaging.name}</span>
      </div>

      {/* Prix par unite de reference (kg, L, pce) */}
      <div className="flex-1 text-sm text-neutral-600 text-right whitespace-nowrap">
        {item.displayUnitPrice !== null
          ? `${formatCentimes(item.displayUnitPrice, currency)}/${item.displayUnitSymbol}`
          : '-'}
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          className="p-1.5"
          onClick={onViewHistory}
          title="Historique des prix"
        >
          <History className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
